 import { TbClock, TbShield, TbShieldOff, TbShieldHalf, TbPencil, TbTrash, TbUsers, TbUserPlus, TbBed, TbCalendar } from "react-icons/tb";

const POLICY_CONFIG = {
  free: {
    label: "Annulation gratuite",
    icon: TbShield,
    bg: "bg-green-50", text: "text-green-700", border: "border-green-200",
    accent: "bg-green-400",
  },
  partial: {
    label: "Partielle",
    icon: TbShieldHalf,
    bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200",
    accent: "bg-amber-400",
  },
  non_refundable: {
    label: "Non remboursable",
    icon: TbShieldOff,
    bg: "bg-red-50", text: "text-red-600", border: "border-red-200",
    accent: "bg-red-400",
  }
};

/* PÉRIODES - unité de vente du tarif.
   "long_stay" est l'ancienne valeur, conservée pour les plans d'avant migration. */
const STAY_CONFIG = {
  short_stay: { label: "À la nuit",    Icon: TbBed,      bg: "bg-gray-50",  text: "text-gray-500",  border: "border-gray-200" },
  weekly:     { label: "À la semaine", Icon: TbCalendar, bg: "bg-[rgb(var(--k-green-rgb)/0.08)]", text: "text-[var(--k-green)]", border: "border-[rgb(var(--k-green-rgb)/0.2)]" },
  monthly:    { label: "Au mois",      Icon: TbCalendar, bg: "bg-[rgb(var(--k-green-rgb)/0.08)]", text: "text-[var(--k-green)]", border: "border-[rgb(var(--k-green-rgb)/0.2)]" },
  long_stay:  { label: "Au mois",      Icon: TbCalendar, bg: "bg-[rgb(var(--k-green-rgb)/0.08)]", text: "text-[var(--k-green)]", border: "border-[rgb(var(--k-green-rgb)/0.2)]" },
};

const MEAL_LABELS = {
  none: null,
  breakfast: { label: "Petit-déjeuner", emoji: "🍳" },
  half_board: { label: "Demi-pension", emoji: "🍽️" },
  full_board: { label: "Pension complète", emoji: "🍱" }
};

const Tag = ({ icon: Icon, label, bg, text, border }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium whitespace-nowrap ${bg} ${text} ${border}`}>
    {Icon && <Icon size={12} className="shrink-0" />}
    {label}
  </span>
);

const RatePlanCard = ({ plan, handleEdit, handleDelete }) => {
  const policyType = plan.cancellationPolicy?.type || "free";
  const policy = POLICY_CONFIG[policyType];
  const PolicyIcon = policy.icon;
  const meal = MEAL_LABELS[plan.mealPlan];
  const guestPolicy = plan.extraGuestPolicy;

  // PÉRIODES
  const stayType = plan.stayType || "short_stay";
  const stay     = STAY_CONFIG[stayType] || STAY_CONFIG.short_stay;
  const isPeriod = stayType !== "short_stay";
  const StayIcon = stay.Icon;

  // Les suppléments par personne ne s'appliquent pas sur un forfait.
  const hasGuestFees = !isPeriod && (guestPolicy?.extraAdultFee > 0 || guestPolicy?.extraChildFee > 0);

  // Le délai se lit en jours sur un forfait, en heures sinon.
  const delayHours = plan.cancellationPolicy?.hoursBeforeArrival || 0;
  const delayLabel = isPeriod
    ? `${Math.round(delayHours / 24)} j avant`
    : `${delayHours} h avant`;

  // FRAIS FIXES - le libellé suit le mode réellement configuré.
  const penaltyMode = plan.cancellationPolicy?.penaltyMode || "percentage";
  const partialLabel = penaltyMode === "fixed"
    ? `Partielle · ${plan.cancellationPolicy?.penaltyFixedAmount || 0} €`
    : `Partielle · ${plan.cancellationPolicy?.penaltyPercentage || 0} %`;

  // Phrase corrigée : l'ancienne version produisait "une semaine entier e".
  const periodHint = stayType === "weekly"
    ? "Prix à saisir pour une semaine entière (7 nuits), pas par nuit."
    : "Prix à saisir pour un mois entier (30 nuits), pas par nuit.";

  const hasTags = meal ||
    (policyType !== "non_refundable" && delayHours > 0) ||
    guestPolicy?.baseOccupancy > 0 ||
    hasGuestFees;

  return (
    /* h-full : même hauteur que les cartes voisines de la même ligne,
       sans s'étirer au-delà grâce à auto-rows-[max-content] sur la grille. */
    <div className="h-full bg-white border border-gray-100 rounded-2xl overflow-hidden hover:border-gray-200 hover:shadow-lg hover:shadow-gray-100/60 hover:-translate-y-0.5 transition-all duration-200 flex flex-col">

      <div className={`h-[3px] w-full ${policy.accent}`} />

      <div className="p-4 flex flex-col gap-3 flex-1">

        {/* HEADER */}
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${policy.bg} ${policy.text} ${policy.border}`}>
              <PolicyIcon size={11} className="shrink-0" />
              {policyType === "partial" ? partialLabel : policy.label}
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${stay.bg} ${stay.text} ${stay.border}`}>
              <StayIcon size={11} className="shrink-0" />
              {stay.label}
            </span>
          </div>
          <h3 className="text-[15px] font-bold text-gray-900 leading-snug">{plan.name}</h3>
          {plan.description && (
            <p className="text-xs text-gray-400 line-clamp-2 leading-relaxed">{plan.description}</p>
          )}
        </div>

        {/* Rappel de l'unité de saisie des prix */}
        {isPeriod && (
          <p className="text-[11px] text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5 leading-relaxed">
            {periodHint}
          </p>
        )}

        {/* TAGS */}
        {hasTags && (
          <div className="flex flex-wrap gap-1.5">
            {policyType !== "non_refundable" && delayHours > 0 && (
              <Tag icon={TbClock} label={delayLabel}
                bg="bg-gray-50" text="text-gray-500" border="border-gray-100" />
            )}
            {meal && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium whitespace-nowrap bg-blue-50 text-blue-700 border-blue-100">
                {meal.emoji} {meal.label}
              </span>
            )}
            {guestPolicy?.baseOccupancy > 0 && (
              <Tag icon={TbUsers} label={`${guestPolicy.baseOccupancy} pers.`}
                bg="bg-purple-50" text="text-purple-700" border="border-purple-100" />
            )}
            {hasGuestFees && (
              <Tag icon={TbUserPlus} label={`+${guestPolicy.extraAdultFee} €/adulte`}
                bg="bg-indigo-50" text="text-indigo-700" border="border-indigo-100" />
            )}
          </div>
        )}

        {/* ACTIONS : même largeur, en bas de carte, Supprimer lisible */}
        <div className="grid grid-cols-2 gap-2 pt-3 mt-auto border-t border-gray-50">
          <button onClick={() => handleEdit(plan)}
            className="inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-[var(--k-green)] text-xs font-semibold hover:bg-[rgb(var(--k-green-rgb)/0.08)] active:scale-[0.98] transition-all duration-150">
            <TbPencil size={14} /> Modifier
          </button>
          <button onClick={() => handleDelete(plan)}
            className="inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-red-500 text-xs font-semibold hover:bg-red-50 active:scale-[0.98] transition-all duration-150">
            <TbTrash size={14} /> Supprimer
          </button>
        </div>

      </div>
    </div>
  );
};

export default RatePlanCard;